import { useState, useEffect } from 'react';
import { usePage } from '../../context/PageContext';
import { t } from '../../utils/i18n';
import arckBg from '../../img/banner.png';
import iconFirst from '../../img/icon-first.png';
import iconSecond from '../../img/icon-second.png';
import iconThird from '../../img/icon-third.png';
import './index.css';

const DEFAULT_CLOSED_SOURCE = [
    { rank: 1, name: 'GPT-5.5', score: '3.30' },
    { rank: 2, name: 'gemini-3.1-pro-preview', score: '2.79' },
    { rank: 3, name: 'gemini-3-flash-preview', score: '2.78' },
];

const DEFAULT_OPEN_SOURCE = [
    { rank: 1, name: 'Kimi-K2.6', score: '2.69' },
    { rank: 2, name: 'DeepSeek-V4-Pro', score: '2.64' },
    { rank: 3, name: 'MiMo-V2.5-Pro', score: '2.43' },
];

/** 从 ARCK index.html 解析排行榜数据，返回 { closed, open } 或 null */
async function fetchArckLeaderboard() {
    const url = `${process.env.PUBLIC_URL || ''}/arck/index.html`;
    const html = await fetch(url).then((r) => r.text());
    const match = html.match(/<!--\s*SCORED:begin\s*-->([\s\S]*?)<!--\s*SCORED:end\s*-->/);
    if (!match) return null;
    const block = match[1];
    const jsonMatch = block.match(/const\s+SCORED\s*=\s*(\{[\s\S]*?\});/);
    if (!jsonMatch) return null;
    const scored = JSON.parse(jsonMatch[1]);
    // scored.models 可能是 Array 或 Object（keyed by model name）
    const rawModels = scored.models;
    const models = Array.isArray(rawModels)
        ? rawModels
        : (rawModels && typeof rawModels === 'object')
            ? Object.entries(rawModels).map(([key, val]) => ({ name: key, ...val }))
            : [];

    const toEntry = (m, idx) => ({
        rank: idx + 1,
        name: m.name || m.model || String(idx + 1),
        score: typeof m.arck?.lsi_overall === 'number'
            ? m.arck.lsi_overall.toFixed(2)
            : '—',
    });

    const closed = models
        .filter((m) => m.type === 'closed')
        .sort((a, b) => (b.arck?.lsi_overall ?? 0) - (a.arck?.lsi_overall ?? 0))
        .slice(0, 3)
        .map(toEntry);

    const open = models
        .filter((m) => m.type === 'open')
        .sort((a, b) => (b.arck?.lsi_overall ?? 0) - (a.arck?.lsi_overall ?? 0))
        .slice(0, 3)
        .map(toEntry);

    return { closed, open };
}

const RANK_ICONS = [iconFirst, iconSecond, iconThird];

function RankBadge({ rank }) {
    return (
        <img className="arck-rank-badge" src={RANK_ICONS[rank - 1]} alt={`#${rank}`} />
    );
}

function LeaderboardColumn({ title, items }) {
    return (
        <div className="arck-lb-col">
            <div className="arck-lb-header">{title}</div>
            <div className="arck-lb-body">
                {items.map((item) => (
                    <div className="arck-lb-row" key={item.name}>
                        <RankBadge rank={item.rank} />
                        <span className="arck-lb-name">{item.name}</span>
                        <span className="arck-lb-score">{item.score}</span>
                    </div>
                ))}
                <div className="arck-lb-more">More <span className="arck-lb-more-arrow" /></div>
            </div>
        </div>
    );
}

export default function ArckBanner() {
    const { navigate } = usePage();
    const [closedSource, setClosedSource] = useState(DEFAULT_CLOSED_SOURCE);
    const [openSource, setOpenSource] = useState(DEFAULT_OPEN_SOURCE);

    useEffect(() => {
        fetchArckLeaderboard()
            .then((data) => {
                if (!data) return;
                if (data.closed && data.closed.length > 0) setClosedSource(data.closed);
                if (data.open && data.open.length > 0) setOpenSource(data.open);
            })
            .catch(() => {
                /* 静默保持默认值 */
            });
    }, []);

    const current = { closed: closedSource, open: openSource };

    const handleClick = (e) => {
        e.preventDefault();
        navigate('/arck');
    };

    return (
        <section className="section arck-banner-section">
            <div className="container">
                <a
                    className="arck-banner"
                    href="/arck"
                    onClick={handleClick}
                    style={{ backgroundImage: `url(${arckBg})` }}
                >
                    <div className="arck-banner-left">
                        <span className="arck-banner-badge">ARCK</span>
                        <h3 className="arck-banner-title">
                            {t(
                                <>ARCK：通往科学智能的<br /><span className="arck-highlight">桥梁</span></>,
                                <>The Arc of Scientific<br /><span className="arck-highlight">Intelligence</span></>
                            )}
                        </h3>
                        <p className="arck-banner-desc">
                            {t(
                                '面向生命科学语言模型的评测框架——以能力为中心，覆盖通用任务、辅助科研与生物设计。',
                                'From benchmark scores to capability maps for life-sciences AI – across reasoning, research workflows, and biological design.'
                            )}
                        </p>
                    </div>
                    <div className="arck-banner-right">
                        <LeaderboardColumn title="CLOSED-SOURCE MODELS" items={current.closed} />
                        <LeaderboardColumn title="OPEN-SOURCE MODELS" items={current.open} />
                    </div>
                </a>
            </div>
        </section>
    );
}
